import { Switch } from '@/components/ui/switch'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useId, useState } from 'react'

import { FxAlert, FxButton, FxIcon, FxPanel } from '@/components/fx'
import { llmApi, templatesApi } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import type { LlmConsent, LlmModel, Template } from '@/types/domain'
import { useCockpitContext } from '../useCockpit'
import { DialogShell } from './DialogShell'
import { SaveHint } from './SaveHint'
import { consentDate, costPerThousandPages, formatUsd } from './format'
import { useTemplateSetting } from './useTemplateSetting'

/**
 * "KI-Zweitmeinung" — what is actually stored about sending this form's
 * pages to a model, plus the two settings that ride on it.
 *
 * Consent is a real object, not a checkbox: `llmConsent {approver, date,
 * provider, region}`. The page pass and the record check are gated on it
 * **and** on the deployment's own `LLM_PASS_ENABLED`; without both, no page
 * leaves the cluster. So this panel reports the stored object field by field
 * — who approved it, when, for which vendor and which region — instead of a
 * toggle that would imply the app can grant or withdraw it. It cannot; see
 * the gaps below.
 *
 * The provider is never overridable. Which vendor may see the pages is a
 * data-protection decision recorded in the consent, and the gateway refuses
 * a run whose provider does not match it — which is why the model picker is
 * filtered to that provider rather than offering every priced model, and why
 * granting is a button that carries no vendor: the gateway stamps the one it
 * runs. Granting and withdrawing are a manager's to do, not an editor's.
 */
export function ConsentSection({
  template,
  canWrite,
  canManage,
  onOpenLog,
  onOpenArms,
}: {
  template: Template
  canWrite: boolean
  canManage: boolean
  onOpenLog: () => void
  onOpenArms: () => void
}) {
  const consent = usableConsent(template.llmConsent)

  return (
    <FxPanel
      title="KI-Zweitmeinung"
      meta={
        consent ? (
          <span className="inline-flex items-center gap-1 rounded-sm bg-success-subtle px-2 py-0.5 text-small text-success-on-subtle">
            <FxIcon name="check_circle" size={16} />
            aktiv
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-sm bg-fill px-2 py-0.5 text-small text-text">
            <FxIcon name="do_not_disturb_on" size={16} />
            nicht aktiv
          </span>
        )
      }
      className="p-5 xs:p-8 sm:p-10"
    >
      {consent ? (
        <ConsentFacts
          consent={consent}
          canManage={canManage}
          onOpenLog={onOpenLog}
          onOpenArms={onOpenArms}
        />
      ) : (
        <NoConsent canManage={canManage} />
      )}

      {consent && (
        <>
          <hr className="my-6 border-0 border-t-2 border-divider" />
          <ModelPicker template={template} consent={consent} canWrite={canWrite} />
        </>
      )}

      <hr className="my-6 border-0 border-t-2 border-divider" />
      <TrainingSwitch allowed={template.trainingAllowed} canWrite={canWrite} />
    </FxPanel>
  )
}

/**
 * A consent object with nothing in it is not consent.
 *
 * `llmConsent` is free-form JSONB; a template can carry `{}` from a
 * migration or a half-finished edit. Treating that as "aktiv" would put a
 * green badge on a template no model will ever run on, because the worker
 * checks the provider and the region against the deployment's own.
 */
export function usableConsent(consent: LlmConsent | null | undefined): LlmConsent | null {
  if (!consent) return null
  const hasSomething = [consent.approver, consent.date, consent.provider, consent.region].some(
    (value) => typeof value === 'string' && value.trim() !== '',
  )
  return hasSomething ? consent : null
}

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic (Claude)',
  google: 'Google Gemini (Vertex AI)',
}

export function providerLabel(provider: string | undefined): string {
  if (!provider) return 'nicht vermerkt'
  return PROVIDER_LABELS[provider] ?? provider
}

function ConsentFacts({
  consent,
  canManage,
  onOpenLog,
  onOpenArms,
}: {
  consent: LlmConsent
  canManage: boolean
  onOpenLog: () => void
  onOpenArms: () => void
}) {
  const [withdrawing, setWithdrawing] = useState(false)
  const approved = [consent.approver, consentDate(consent.date) && `am ${consentDate(consent.date)}`]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-body xs:grid-cols-[180px_minmax(0,1fr)]">
        <dt className="text-text-muted xs:text-text">Anbieter</dt>
        <dd className="m-0 font-semibold">{providerLabel(consent.provider)}</dd>
        <dt className="text-text-muted xs:text-text">Region</dt>
        <dd className="m-0 font-semibold">{consent.region?.trim() || 'nicht vermerkt'}</dd>
        <dt className="text-text-muted xs:text-text">Freigegeben von</dt>
        <dd className="m-0 font-semibold">{approved || 'nicht vermerkt'}</dd>
      </dl>

      <p className="my-4 text-small text-text-muted">
        Ohne diese Freigabe verlässt keine Seite den Server. Zusätzlich muss die Funktion auf dem
        Server eingeschaltet sein — ob sie läuft, sehen Sie am Protokoll: dort steht jeder Aufruf,
        auch ein fehlgeschlagener.
      </p>

      <div className="flex flex-wrap gap-3">
        <FxButton variant="tonal" icon="receipt_long" onClick={onOpenLog}>
          Protokoll ansehen
        </FxButton>
        <FxButton variant="tonal" icon="compare_arrows" onClick={onOpenArms}>
          Lesungen vergleichen
        </FxButton>
        {canManage && (
          <FxButton variant="outlined" onClick={() => setWithdrawing(true)}>
            Freigabe zurückziehen
          </FxButton>
        )}
      </div>

      <WithdrawDialog open={withdrawing} onClose={() => setWithdrawing(false)} />
    </>
  )
}

/**
 * Granting and withdrawing, as one mutation each.
 *
 * Both re-read the template afterwards rather than patching the cache: the
 * badge above is drawn from the stored consent, and the one thing this panel
 * must never do is show a release the gateway did not make.
 */
function useConsentChange(action: 'grant' | 'withdraw') {
  const cockpit = useCockpitContext()
  return useMutation({
    mutationFn: async () => {
      if (action === 'grant') await templatesApi.grantLlmConsent(cockpit.templateId)
      else await templatesApi.withdrawLlmConsent(cockpit.templateId)
    },
    onSuccess: () => cockpit.invalidate.template(),
  })
}

/** What a refusal means, in words the person can act on. */
export function consentErrorText(error: unknown): string {
  const api = ApiError.from(error)
  if (api.code === 'llm_disabled')
    return 'Auf diesem Server ist kein KI-Anbieter eingerichtet. Es gibt zurzeit nichts freizugeben.'
  if (api.code === 'unsupported_backend') return 'Dieser Server speichert keine Freigaben.'
  if (api.isForbidden)
    return 'Dafür fehlt Ihnen die Berechtigung — die Freigabe erteilt, wer dieses Musterformular verwaltet.'
  return 'Das hat nicht geklappt. Bitte versuchen Sie es noch einmal.'
}

/**
 * Withdrawing, which this dialog could until now only apologise for.
 *
 * The PATCH handler forwards `llmConsent` only when it is a non-nil map, so
 * `{"llmConsent": null}` was accepted with a 200 and changed nothing — a
 * button that reports success and leaves the release in place is the worst
 * possible version of this control, so it used to say so and send people to
 * the operator instead. `DELETE /v1/templates/:id/llm-consent` puts the
 * column back to NULL, which `{}` would not: an empty object is still a row
 * that reads as a decision somebody made.
 *
 * It asks first because the cost is asymmetric. Withdrawing stops the page
 * pass on the next run, quietly changing what this form's readings are worth;
 * granting again is one click, but the readings taken in between are not
 * retaken by themselves.
 */
function WithdrawDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const withdraw = useConsentChange('withdraw')

  return (
    <DialogShell open={open} onClose={onClose} title="Freigabe zurückziehen">
      <div className="flex flex-col gap-4 text-body text-text">
        <p className="m-0">
          Danach verlässt keine Seite dieses Musterformulars mehr den Server. Die KI-Prüfung lässt
          sich nicht mehr starten; bereits gespeicherte Lesungen bleiben erhalten und weiter
          einsehbar.
        </p>
        <p className="m-0">
          Sie können die Freigabe jederzeit neu erteilen — die zurückgezogene wird dabei nicht
          wiederhergestellt, sondern eine neue mit Datum und Ihrem Namen festgehalten. Was bisher an
          ein Modell ging, steht unverändert unter „Protokoll und Kosten“.
        </p>
        {withdraw.isError && (
          <p className="m-0 text-small text-danger-text" role="alert">
            {consentErrorText(withdraw.error)}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <FxButton variant="tonal" onClick={onClose} disabled={withdraw.isPending}>
            Abbrechen
          </FxButton>
          <FxButton
            variant="dangerTonal"
            disabled={withdraw.isPending}
            onClick={() => withdraw.mutate(undefined, { onSuccess: onClose })}
          >
            {withdraw.isPending ? 'Wird zurückgezogen…' : 'Freigabe zurückziehen'}
          </FxButton>
        </div>
      </div>
    </DialogShell>
  )
}

/**
 * No release yet — and, until now, no way to give one.
 *
 * This panel used to send people to "den Betreiber", which was accurate while
 * consent was granted by hand in the database. It stopped being tenable once
 * the cockpit offered "KI-Bildprüfung" as a button: on an unreleased form the
 * only possible outcome was a failed job, reading `arm llm_image produced
 * nothing`.
 *
 * The button sends no vendor and no date. The gateway stamps both from its own
 * configuration, because the worker checks the stored record against them and
 * refuses a run that does not match — a consent the client composed could name
 * a vendor this deployment does not run, and would then be refused every time
 * with nothing on screen to explain it.
 */
function NoConsent({ canManage }: { canManage: boolean }) {
  const grant = useConsentChange('grant')

  return (
    <FxAlert tone="info" title="Für dieses Musterformular nicht freigegeben">
      <p className="m-0">
        Die KI-Zweitmeinung wird pro Musterformular freigegeben. Die Freigabe hält fest, wer sie
        erteilt hat, für welchen Anbieter und für welche Region — bis dahin verlässt keine Seite
        dieses Formulars den Server.
      </p>
      {canManage ? (
        <div className="mt-4 flex flex-col gap-2">
          <div>
            <FxButton icon="verified_user" disabled={grant.isPending} onClick={() => grant.mutate()}>
              {grant.isPending ? 'Wird freigegeben…' : 'Jetzt freigeben'}
            </FxButton>
          </div>
          {grant.isError && (
            <p className="m-0 text-small text-danger-text" role="alert">
              {consentErrorText(grant.error)}
            </p>
          )}
        </div>
      ) : (
        <p className="m-0 mt-3">Erteilen kann sie, wer dieses Musterformular verwaltet.</p>
      )}
    </FxAlert>
  )
}

/**
 * Which model reads this form's pages.
 *
 * Three things decide it, in this order: the model a job was started with,
 * then this setting, then the deployment's `LLM_PASS_MODEL`. The line under
 * the picker says which of the three is in force, because "Voreinstellung
 * des Servers" and a chosen model look the same once saved.
 *
 * The list is `GET /v1/llm/models` — the gateway's price table — filtered to
 * the provider the consent names. Both entry points validate against that
 * same table (400 `unknown_model`), because a model nobody can price is a
 * run nobody can cost, and a typo would otherwise reach the provider as a
 * 404 in the middle of a few hundred pages.
 */
function ModelPicker({
  template,
  consent,
  canWrite,
}: {
  template: Template
  consent: LlmConsent
  canWrite: boolean
}) {
  const selectId = useId()
  const save = useTemplateSetting()
  const models = useQuery({
    queryKey: ['llm', 'models'],
    queryFn: () => llmApi.models(),
    staleTime: 60 * 60_000,
  })

  const provider = consent.provider?.trim()
  const all = models.data ?? []
  const offered: LlmModel[] = provider ? all.filter((model) => model.provider === provider) : all
  const current = template.llmModel?.trim() ?? ''
  const known = offered.some((model) => model.id === current)

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={selectId} className="text-body-strong text-text">
        Modell
      </label>
      <select
        id={selectId}
        className="h-control w-full max-w-[420px] rounded-md bg-ground px-3 text-body text-text"
        value={current}
        disabled={!canWrite || save.isPending || models.isLoading}
        onChange={(event) => {
          const next = event.target.value
          // "" is the deployment default, and the gateway drops an empty
          // llmModel (see the note below), so it is never sent.
          if (next === '') return
          save.mutate({ llmModel: next })
        }}
      >
        {/* Offered but not selectable once a model is set: the PATCH handler
            only forwards a non-empty llmModel, so choosing it would report
            success and leave the old model in place. */}
        <option value="" disabled={current !== ''}>
          Voreinstellung des Servers
        </option>
        {current !== '' && !known && (
          // A model stored by someone else, or one that has left the price
          // table. Hiding it would make the select show the wrong answer.
          <option value={current}>{current} · nicht mehr in der Preisliste</option>
        )}
        {offered.map((model) => (
          <option key={model.id} value={model.id}>
            {model.id} · ca. {formatUsd(costPerThousandPages(model))} / 1000 Seiten
          </option>
        ))}
      </select>

      <p className="text-small text-text-muted">
        {current === ''
          ? 'Zurzeit gilt die Voreinstellung des Servers. Ein Lauf kann ein anderes Modell mitgeben; sonst gilt, was hier steht.'
          : `Zurzeit gilt „${current}“ für dieses Musterformular. Ein einzelner Lauf kann davon abweichen.`}{' '}
        Preise sind Listenpreise — eine Schätzung, gerechnet mit rund 4.400 Eingabe- und 1.500
        Ausgabe-Token je Seite.
      </p>
      {current !== '' && (
        <p className="text-small text-text-muted">
          Auf die Voreinstellung des Servers zurückstellen kann zurzeit nur der Betreiber.
        </p>
      )}
      {models.isError && (
        <p className="text-small text-danger-text" role="alert">
          Die Modell-Liste konnte nicht geladen werden. Die gespeicherte Einstellung bleibt
          unverändert.
        </p>
      )}
      {offered.length === 0 && !models.isLoading && !models.isError && (
        <p className="text-small text-text-muted">
          Für den freigegebenen Anbieter ({providerLabel(provider)}) führt dieser Server kein Modell
          in seiner Preisliste.
        </p>
      )}
      {canWrite && <SaveHint mutation={save} saved="Modell gespeichert" />}
    </div>
  )
}

/**
 * The training corpus is opt-**out**: `trainingAllowed` defaults to true and
 * turning it off stops capture, backfill and export for this template.
 *
 * Not optimistic. The Flutter switch flips first and rolls back on failure,
 * which for a consent flag means the screen can claim, for a second, that
 * capture is off while it is on. Here the switch moves when the gateway has
 * said so.
 */
function TrainingSwitch({ allowed, canWrite }: { allowed: boolean; canWrite: boolean }) {
  const labelId = useId()
  const save = useTemplateSetting()

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-4 sm:items-center">
        <div className="flex-1">
          <span id={labelId} className="text-body-strong text-text">
            Trainingsdaten
          </span>
          <p className="mt-1 text-small text-text-muted">
            Feldausschnitte und Ihre Korrekturen verbessern die Erkennung. Sie bleiben in einem
            getrennten Speicher und werden nicht weitergegeben.
          </p>
        </div>
        <Switch
          aria-labelledby={labelId}
          checked={allowed}
          disabled={!canWrite || save.isPending}
          onCheckedChange={(next) => save.mutate({ trainingAllowed: next })}
          className="h-7 w-12 shrink-0 data-[state=checked]:bg-primary-strong data-[state=unchecked]:bg-fill [&>span]:h-6 [&>span]:w-6 [&>span]:data-[state=checked]:translate-x-5"
        />
      </div>
      {canWrite && (
        <SaveHint
          mutation={save}
          saved={allowed ? 'Trainingsdaten erlaubt' : 'Trainingsdaten abgeschaltet'}
        />
      )}
    </div>
  )
}
